import { FC, useMemo } from 'react'
import cn from 'classnames'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import type { LeadProfileLead } from '../../LeadProfileModal.types'
import { useLeadRefetch } from '../../LeadRefetchContext'
import { parseLeadStateFromCardFunnelState } from '../../leadFunnelId.utils'
import classes from './index.module.css'

const LEAD_STATE_META: Record<string, { label: string; background: string }> = {
  new: {
    label: 'В новых',
    background: '#9747ff',
  },
  in_work: {
    label: 'В работе',
    background: '#f97316',
  },
  processed: {
    label: 'В обработанных',
    background: 'rgb(108, 206, 103)',
  },
  client: {
    label: 'В Клиентах',
    background: '#c026d3',
  },
}

type LeadFunnelMetaRowProps = {
  lead: LeadProfileLead
  className?: string
}

const LeadFunnelMetaRow: FC<LeadFunnelMetaRowProps> = ({ lead, className }) => {
  const {
    resolveLeadFunnelId,
    selectedFunnelId,
    getCardInFunnelSnapshot,
    cardFunnelSnapshotEpoch,
  } = useLeadRefetch()

  const selectedLeadFunnelId = useMemo(
    () => resolveLeadFunnelId({ lead, selectedFunnelId }) ?? null,
    [lead, selectedFunnelId, resolveLeadFunnelId],
  )

  const leadStateMeta = useMemo(() => {
    if (selectedFunnelId === null) return null
    const cardFunnel = getCardInFunnelSnapshot(selectedFunnelId)
    const key = parseLeadStateFromCardFunnelState(cardFunnel?.state)
    if (!key) return null
    return LEAD_STATE_META[key] ?? null
  }, [
    cardFunnelSnapshotEpoch,
    getCardInFunnelSnapshot,
    selectedFunnelId,
  ])

  const leadId = lead.id ?? null

  return (
    <div
      className={cn(classes.metaRowCompact, className)}
      role="group"
      aria-label="Состояние, ID заявки и ID лида"
    >
      <TooltipWrapper title="Состояние заявки" placement="top">
        <span className={classes.metaRowCompactItem}>
          {leadStateMeta ? (
            <span
              className={cn(
                classes.leadStateBadge,
                classes.idBadge,
                classes.metaRowCompactState,
              )}
              style={{
                color: 'var(--color-on-accent)',
                backgroundColor: leadStateMeta.background,
              }}
            >
              {leadStateMeta.label}
            </span>
          ) : (
            <span className={classes.metaRowCompactPlain}>—</span>
          )}
        </span>
      </TooltipWrapper>

      <span className={classes.metaRowCompactDivider} aria-hidden />

      <TooltipWrapper title="ID заявки" placement="top">
        <span
          className={cn(
            classes.bindingIdChip,
            classes.idBadge,
            classes.metaRowCompactChip,
          )}
        >
          {selectedLeadFunnelId ?? '—'}
        </span>
      </TooltipWrapper>

      <span className={classes.metaRowCompactDivider} aria-hidden />

      <TooltipWrapper title="ID лида" placement="top">
        <span
          className={cn(
            classes.bindingIdChip,
            classes.idBadge,
            classes.metaRowCompactChip,
          )}
        >
          {leadId ?? '—'}
        </span>
      </TooltipWrapper>
    </div>
  )
}


export default LeadFunnelMetaRow
